'use client';
import React from 'react';
import Link, { useLinkStatus } from 'next/link';
import { Button, ButtonProps } from 'antd';
import styles from './button.module.scss';

type ButtonPropsWithoutIconPosition = Omit<ButtonProps, 'iconPosition' | 'variant'>;

interface ThemedButtonProps extends ButtonPropsWithoutIconPosition {
  theme?: 'primary' | 'secondary' | 'accent';
  variant?: 'solid' | 'outlined' | 'outline'; 
  text: string;
  icon?: React.ReactNode;
  iconPosition?: 'prefix' | 'suffix';
  loading?: boolean;
  href?: string;
  external?: boolean;
  onImage?: boolean;
  loadingOnNavigate?: boolean;
}

const isExternalHref = (href: string) =>
  /^([a-z]+:)?\/\//i.test(href) ||
  href.startsWith('mailto:') ||
  href.startsWith('tel:');

const LinkPending: React.FC<{
  render: (pending: boolean) => React.ReactNode;
}> = ({ render }) => {
  const { pending } = useLinkStatus();

  return <>{render(pending)}</>;
};

const ThemedButton: React.FC<ThemedButtonProps> = ({
  theme = 'primary',
  variant = 'solid',
  text,
  icon,
  iconPosition = 'prefix',
  loading = false,
  className,
  href,
  external,
  disabled,
  onImage = false,
  loadingOnNavigate = false,
  ...rest
}) => {
  const normalizedVariant = variant === 'outlined' ? 'outline' : variant;

  const renderButton = (isLoading: boolean) => {
    const hasIcon = !!icon && !isLoading;

    const classes = [
      styles.btn,
      hasIcon ? styles.hasIcon : '',
      onImage ? styles.onImage : '',
      className || '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Button
        type="default"
        className={classes}
        data-theme={theme}
        data-variant={normalizedVariant}
        loading={isLoading}
        disabled={isLoading || !!disabled}
        aria-busy={isLoading || undefined}
        aria-disabled={isLoading || !!disabled || undefined}
        {...rest}
      >
        {hasIcon && iconPosition === 'prefix' && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        <span className={styles.label}>{text}</span>
        {hasIcon && iconPosition === 'suffix' && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
      </Button>
    );
  };

  const btn = renderButton(loading);

  if (!href) return btn;

  const isExt = typeof external === 'boolean' ? external : isExternalHref(href);
  return isExt ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.linkWrapper}
    >
      {btn}
    </a>
  ) : (
    <Link href={href} className={styles.linkWrapper}>
      {loadingOnNavigate ? (
        <LinkPending render={(pending) => renderButton(loading || pending)} />
      ) : (
        btn
      )}
    </Link>
  );
};

export default ThemedButton;
